/**
 * One `RecipientNotice`, rendered.
 *
 * Every sentence in it is decided in `lib/recipient-experience.ts` and none of
 * it is decided here — this component chooses a tone class and a paragraph gap,
 * and that is the whole of its judgement. The reason for the split is the reason
 * `template-display.ts` exists: the editor, the preview and the test-send panel
 * all have to describe the same message, slice 4's sentence was printed in three
 * of them, and three components each deciding when the sentence is still true is
 * three chances to leave one of them saying the old thing.
 *
 * **It is deliberately not a `variant` prop on one big component.** The three
 * screens ask genuinely different questions — *what does this carry*, *what can
 * the preview not show you*, *what should you look for in the test that
 * arrives* — and each gets its own function in the copy module. This renders
 * whichever block it is handed.
 *
 * **`live` is left at `Callout`'s default of `off`, and that is a decision
 * rather than an omission.** This notice is already on screen when the tab is
 * opened, so it is met in reading order like any other prose; the only thing
 * that changes it is the workspace's tracking settings, which do not change
 * while somebody is reading a template. Passing `live="polite"` would make every
 * screen reader announce a paragraph about tracking pixels on top of the page
 * load it is already reading out. The prop is not passed because the default is
 * right — not because nobody considered it.
 */
import { Callout } from '@wend/ui';
import { Link } from 'react-router-dom';

import { integrationTabPath } from '../integrations/integration-tab-url';

import type { RecipientNotice } from '../../lib/recipient-experience';

export function RecipientTrackingNotice({ notice }: { notice: RecipientNotice }) {
  return (
    <Callout tone={notice.tone} title={notice.title}>
      <div className="flex max-w-prose flex-col gap-2">
        {notice.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        {/*
          SLICE 29: the two arms that describe a SETUP problem rather than a
          recipient experience now carry a link instead of the paragraphs that
          described the setup. This is a compose surface: the person is writing
          an email, and the repair is on another screen entirely.
        */}
        {notice.pointsAtIntegrations && (
          <p>
            <Link
              to={integrationTabPath('email')}
              className="text-text underline underline-offset-2"
            >
              Check tracking on Integrations
            </Link>
          </p>
        )}
      </div>
    </Callout>
  );
}
